// authFetch — a thin wrapper around fetch() that attaches the current
// Firebase user's ID token as `Authorization: Bearer ...`.
//
// Privacy by Design (memory: feedback_privacy_by_design): the backend
// is rolling out require_user_token() in shadow mode. Once every
// authenticated request carries a Bearer token, AUTH_ENFORCE=true on
// Render flips verification from log-only to 401-on-mismatch.
//
// USAGE:
//   import authFetch from '../utils/authFetch';
//   const res = await authFetch(`${API}/profile?user_id=${uid}`);
//
// The wrapper is intentionally signature-compatible with fetch(): the
// existing call sites only need `fetch(...)` → `authFetch(...)`.
//
// EDGE CASES:
//   - No signed-in user (pre-auth screens, public endpoints): falls back
//     to a plain fetch() — no header attached.
//   - Token expired (Firebase refreshes silently up to ~1h): we always
//     ask for a fresh token via getIdToken(false) which returns the
//     cached one if still valid and refreshes otherwise.
//   - getIdToken throws (rare — usually a network blip): we let the
//     request go through unauthenticated so the user isn't blocked by a
//     transient Firebase failure. Backend shadow mode will log it; once
//     we flip to enforce, this falls back to a clear 401.

import { getAuth } from 'firebase/auth';

// Backends we're authorized to send our Firebase ID token to. Anything
// else (Strava, Stripe, third-party CDN, analytics) gets a plain fetch
// so we don't leak the token cross-origin. Add new hostnames here when
// the backend lives somewhere new.
const TRUSTED_HOSTS = [
  'claude-coach-backend-1.onrender.com',
  'localhost',
  '127.0.0.1',
];

function isTrustedUrl(input) {
  try {
    const url = typeof input === 'string' ? new URL(input, window.location.origin) : new URL(input.url);
    return TRUSTED_HOSTS.includes(url.hostname);
  } catch {
    // Relative URLs land here on some Safari versions — they hit our
    // origin, so trust them. (Vercel never serves backend routes from
    // the frontend domain, but if someone misconfigures it this fails
    // safe in shadow mode.)
    return true;
  }
}

export default async function authFetch(input, init = {}) {
  if (!isTrustedUrl(input)) {
    return fetch(input, init);
  }

  let token = null;
  try {
    const user = getAuth().currentUser;
    if (user) {
      // false = use cached token if still valid; refresh only when stale.
      token = await user.getIdToken(false);
    }
  } catch (err) {
    // Don't block the request — backend will respond appropriately in
    // enforce mode, and shadow mode just logs the missing token.
    console.warn('authFetch: getIdToken failed, sending unauthenticated request', err);
  }

  const headers = new Headers(init.headers || {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(input, { ...init, headers });
}
